import { captureException } from "@sentry/react";
import { MINUTES_IN_HOUR } from "emoreg/organisms/calendar/constants/time-constants";
import { upperFirst } from "lodash";
import { DateTime, Duration } from "luxon";
import moment from "moment";

import { handlePlural } from "./formatters";

export const DAILY = "DAILY";
export const WEEKLY = "WEEKLY";
export const MONTHLY = "MONTHLY";
export const YEARLY = "YEARLY";
export const ALL_TIME = "ALL_TIME";

const PERIODS = [DAILY, WEEKLY, MONTHLY, YEARLY, ALL_TIME];

export const DISPLAY_DATE_FORMAT = "DD[/]MM[/]YYYY";
export const DISPLAY_TIME_DATE_FORMAT = "HH'h'mm - dd/MM/yyyy";
export const DISPLAY_DATETIME_FORMAT = "dd/MM/yyyy 'à' HH'h'mm";
export const DISPLAY_SHORT_DATETIME_FORMAT = "dd/MM/yyyy HH:mm";
export const DISPLAY_MONTH_FORMAT = "MMMM yyyy";
export const DISPLAY_SHORT_MONTH_FORMAT = "MM/yyyy";
export const DISPLAY_SHORT_DATE_FORMAT = "dd/MM/yyyy";
export const DISPLAY_SHORT_TIME_FORMAT = "HH:mm";

export const INPUT_MONTH_FORMAT = "yyyy-MM";
export const INPUT_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm";
export const INPUT_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Return the sort integer between datishA and datishB.
 * @param {datishA} date - A date to compare
 * @param {datishB} date - A date to compare
 * @returns {integer} Integer sorting datishA and datishB
 */
export function dateSort(datishA, datishB) {
  datishA = datishA === null ? moment(new Date(0)) : datishA;
  datishB = datishB === null ? moment(new Date(0)) : datishB;
  if (datishA.isBefore(datishB)) {
    return -1;
  }
  if (datishA.isAfter(datishB)) {
    return 1;
  }
  return 0;
}

/**
 * Return the label corresponding to a period.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date (moment object) inside the wanted period
 * @returns {string} Label corresponding to the given period type and date
 */
export function periodLabel(periodType, date) {
  if (!PERIODS.includes(periodType)) {
    captureException(new Error(`Unrecognized period type: ${periodType}`));
    return ALL_TIME;
  }

  if (periodType === ALL_TIME) {
    return ALL_TIME;
  }

  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  if (periodType === DAILY) {
    return date.format("YYYY-MM-DD");
  }

  if (periodType === WEEKLY) {
    return date.format("GGGG[-W]WW");
  }

  if (periodType === MONTHLY) {
    return date.format("YYYY-MM");
  }

  if (periodType === YEARLY) {
    return date.format("YYYY");
  }
}

/**
 * Return the humanly readable string corresponding to the start and the end of a period.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date (moment object) inside the wanted period
 * @returns {string} Label corresponding to the given period type and date
 */
export function periodHumanDates(periodType, date) {
  if (!PERIODS.includes(periodType)) {
    throw new Error(`Unrecognized period type: ${periodType}`);
  }

  if (periodType === ALL_TIME) {
    return "Depuis le tout début";
  }

  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  if (periodType === DAILY) {
    return date.format(DISPLAY_DATE_FORMAT);
  }

  const firstDay = firstDayOfPeriod(periodType, date),
    lastDay = lastDayOfPeriod(periodType, date);
  return `${firstDay?.format(DISPLAY_DATE_FORMAT)} - ${lastDay?.format(DISPLAY_DATE_FORMAT)}`;
}

/**
 * Return the date (moment object) of the first day of the given period.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date inside the wanted period
 * @returns {moment} Date (moment object) of the first day of the given period
 */
export function firstDayOfPeriod(periodType, date): any {
  if (!PERIODS.includes(periodType)) {
    throw new Error(`Unrecognized period type: ${periodType}`);
  }
  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  if (periodType === DAILY) {
    // We clone it to avoid surprises... moment objects are mutable.
    return moment(date);
  }

  if (periodType === WEEKLY) {
    return moment(date.format("GGGGWW1"), "GGGGWWE");
  }

  if (periodType === MONTHLY) {
    return moment(date.format("YYYYMM01"), "YYYYMMDD");
  }

  if (periodType === YEARLY) {
    return moment(date.format("YYYY0101"), "YYYYMMDD");
  }
}

/**
 * Return the date (moment object) of the last day of the given period.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date inside the wanted period
 * @returns {moment} Date (moment object) of the last day of the given period
 */
export function lastDayOfPeriod(periodType, date) {
  if (!PERIODS.includes(periodType)) {
    throw new Error("Unrecognized `periodType`");
  }
  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  if (periodType === DAILY) {
    // We clone it to avoid surprises... moment objects are mutable.
    return moment(date);
  }

  if (periodType === WEEKLY) {
    return moment(date.format("GGGGWW7"), "GGGGWWE");
  }

  if (periodType === MONTHLY) {
    let rv = firstDayOfPeriod(periodType, date);
    rv?.add(1, "months").subtract(1, "days");
    return rv;
  }

  if (periodType === YEARLY) {
    let rv = firstDayOfPeriod(periodType, date);
    rv?.add(1, "years").subtract(1, "days");
    return rv;
  }
}

/**
 * Return the date (moment object) of the first day of the next period.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date inside the reference period
 * @returns {moment} Date (moment object) of the first day of the next period
 */
export function firstDayOfNextPeriod(periodType, date) {
  if (!PERIODS.includes(periodType)) {
    throw new Error(`Unrecognized period type: ${periodType}`);
  }

  if (periodType === ALL_TIME) {
    return moment(new Date(2090, 5, 27));
  }

  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  if (periodType === DAILY) {
    return firstDayOfPeriod(periodType, date)?.add(1, "days");
  }

  if (periodType === WEEKLY) {
    return firstDayOfPeriod(periodType, date)?.add(7, "days");
  }

  if (periodType === MONTHLY) {
    return firstDayOfPeriod(periodType, date)?.add(1, "months");
  }

  if (periodType === YEARLY) {
    return firstDayOfPeriod(periodType, date)?.add(1, "years");
  }
}

/**
 * Return the date (moment object) of the first day of the previous period if
 * not before the 01/06/2016, otherwise return null.
 * @param {string} periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param {moment} date - A date inside the reference period
 * @returns {moment} Date (moment object) of the first day of the next period
 */
export function firstDayOfPreviousPeriod(periodType, date) {
  if (!PERIODS.includes(periodType)) {
    throw new Error(`Unrecognized period type: ${periodType}`);
  }

  if (periodType === ALL_TIME) {
    return null;
  }

  if (!moment.isMoment(date)) {
    throw new Error("Unrecognized `date`");
  }

  let rv;

  if (periodType === DAILY) {
    rv = firstDayOfPeriod(periodType, date)?.subtract(1, "days");
  }

  if (periodType === WEEKLY) {
    rv = firstDayOfPeriod(periodType, date)?.subtract(7, "days");
  }

  if (periodType === MONTHLY) {
    rv = firstDayOfPeriod(periodType, date)?.subtract(1, "months");
  }

  if (periodType === YEARLY) {
    rv = firstDayOfPeriod(periodType, date)?.subtract(1, "years");
  }

  /*
   * Weird comparison to deal with moment objects (which are datetimes and
   * not dates)
   */
  if (rv.format("YYYYMMDD") >= "20160601") {
    return rv;
  }
  return null;
}

export const periodTypeToLabelMap = {
  WEEKLY: "Hebdomadaire",
  MONTHLY: "Mensuel",
  DAILY: "Quotidien",
  ALL_TIME: "Tout",
};

export const humanShortDate = (dt, format = "dd/MM") => {
  const datish = dt.startOf("day");
  const today = DateTime.local().startOf("day");
  const yesterday = today.minus({ days: 1 }).startOf("day");
  const tomorrow = today.plus({ days: 1 }).startOf("day");
  if (+datish === +yesterday) {
    return "hier";
  } else if (+datish === +today) {
    return "aujourd'hui";
  } else if (+datish === +tomorrow) {
    return "demain";
  }
  return dt.toFormat(format);
};

export const humanShortDatetime = (dt, date_format = "dd/MM", time_format = "hh:mm") => {
  const date = humanShortDate(dt, date_format);
  const time = dt.toFormat(time_format);
  return `${date}${time}`;
};

type FormatRelativeDateOptions = {
  capitalize?: boolean;
  format?: string;
};

export const formatRelativeDate = (date: DateTime, options?: FormatRelativeDateOptions): string => {
  const { capitalize = true, format = "dd LLL yyyy" } = options ?? {};
  const today = DateTime.now().startOf("day");
  const targetDate = date.startOf("day");

  if (targetDate.equals(today)) {
    return capitalize ? "Aujourd'hui" : "aujourd'hui";
  }

  if (targetDate.equals(today.minus({ days: 1 }))) {
    return capitalize ? "Hier" : "hier";
  }

  return date.toFormat(format, { locale: "fr" });
};

export const formatTotalExperience = (year: number): string => {
  const month = year * 12;
  return monthsToYear(month);
};

export const monthsToYear = (seniority: number): string => {
  const year = Math.trunc(seniority / 12);
  const month = seniority % 12;
  if (year > 0) {
    return year + ` an${year > 1 ? "s" : ""} ${month > 0 ? month + " mois" : ""}`;
  } else if (month > 0) {
    return month + " mois";
  } else {
    return "< 1 mois";
  }
};

export const formatInterventionDuration = (
  startAt?: DateTime | null,
  endAt?: DateTime | null
): string => {
  if (startAt && endAt) {
    const duration = endAt.diff(startAt, ["hours", "minutes"]);
    return duration.toFormat("h'h'mm");
  } else return "?";
};

export const isMidnightDatetime = (datetime: DateTime): boolean => {
  return datetime.get("hour") === 0 && datetime.get("minute") === 0 && datetime.get("second") === 0;
};

export const isNoonDatetime = (datetime: DateTime): boolean => {
  return (
    datetime.get("hour") === 12 && datetime.get("minute") === 0 && datetime.get("second") === 0
  );
};

export const getHoursAndMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / MINUTES_IN_HOUR);
  const remainingMinutes = minutes % MINUTES_IN_HOUR;
  return { hours, minutes: remainingMinutes };
};

/**
 * @description This function is taking hour and minutes and format it with luxon tokens. We are preventing 00min and 00h10min
 */
export const hourMinutesToString = (hours?: number, minutes?: number) => {
  const templateStringHours = hours && hours > 0 ? "h'h'" : "";
  const templateStringMinutes = minutes && minutes > 0 ? "mm'min'" : "";
  const templateString = [templateStringHours, templateStringMinutes].join("");

  return Duration.fromObject({
    hours: hours,
    minutes: minutes,
  }).toFormat(templateString);
};

/**
 * @description Convert a date moment to iso datetime. Optionnally add days to initial date.
 */
const dateToIsoDatetime = (date: moment.Moment, daysNumberToAdd?: number): string => {
  var datetime = DateTime.fromISO(date.format(INPUT_DATE_FORMAT));

  if (daysNumberToAdd) {
    datetime = datetime.plus({ days: daysNumberToAdd });
  }

  return datetime.startOf("day").toISO();
};

/**
 * @description Get filter datetime range from a reference date and a period's type ("DAILY" | "WEEKLY" | "MONTHLY")
 */
export const getFilterDatetimeRange = (
  periodType: string | undefined,
  referenceDate: string | undefined
): [string, string] => {
  const firstDay = firstDayOfPeriod(periodType, moment(referenceDate));
  const lastDay = lastDayOfPeriod(periodType, moment(referenceDate));

  return [dateToIsoDatetime(firstDay), dateToIsoDatetime(lastDay, 1)];
};

/**
 * @description Returns the number of days elapsed since the provided datetime
 */
export const getDaysSince = (datetime: DateTime): number =>
  DateTime.now().diff(datetime).as("days");

/**
 * @description Returns the number of months elapsed since the provided datetime
 */
const getMonthsSince = (datetime: DateTime): number => {
  const months = DateTime.now().diff(datetime, "months").toObject().months;
  return months ? Math.floor(months) : 0;
};

/**
 * @description Returns the number of years elapsed since the provided datetime
 */
const getYearsSince = (datetime: DateTime): number => {
  const years = DateTime.now().diff(datetime, "years").toObject().years;
  return years ? Math.floor(years) : 0;
};

/**
 *
 * @param date
 * @returns date since in human readable format (e.g. 2 jours, 1 mois)
 */
export const getElapsedDurationLabel = (date: DateTime, elapsedPrefix = "Depuis") => {
  const numberOfDays = Math.floor(getDaysSince(date));
  const isToday = DateTime.now().hasSame(date, "day");

  if (isToday) {
    return "Aujourd'hui";
  }

  if (numberOfDays <= 30) {
    return `${elapsedPrefix} ${numberOfDays} ${handlePlural(numberOfDays, {
      plural: "jours",
      singular: "jour",
    })}`;
  }

  if (numberOfDays < 365) {
    const numberOfMonths = getMonthsSince(date);
    return `${elapsedPrefix} ${getMonthsSince(date)} ${handlePlural(numberOfMonths, {
      plural: "mois",
      singular: "mois",
    })}`;
  }

  const numberOfYears = getYearsSince(date);
  return `${elapsedPrefix} ${numberOfYears} ${handlePlural(numberOfYears, {
    plural: "ans",
    singular: "an",
  })}`;
};

export const addWorkingDays = (date: DateTime, days: number) => {
  let remainingDays = days;
  while (remainingDays > 0) {
    date = date.plus({ days: 1 });
    if (![6, 7].includes(date.weekday)) {
      remainingDays -= 1;
    }
  }
  return date;
};

export type Weekdays =
  | "lundi"
  | "mardi"
  | "mercredi"
  | "jeudi"
  | "vendredi"
  | "samedi"
  | "dimanche";

/**
 * @description Converts a week day to its ISO 8601 code.
 * From ISO 8601 : https://en.wikipedia.org/wiki/ISO_8601#Week_dates
 */
export const weekDay2IsoCode = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/**
 * Formats a date range into a human-readable French string
 * @param startsOn - The start date of the range
 * @param endsOn - The end date of the range (optional)
 * @returns A formatted string representing the date range:
 * - For current periods with end date: "jusqu'au DD/MM/YY"
 * - For future periods with end date: "à partir du DD/MM/YY jusqu'au DD/MM/YY"
 * - For future periods without end date: "à partir du DD/MM/YY"
 * - For past periods without end date: empty string
 */
export const formatDateRange = (startsOn: DateTime, endsOn: DateTime | null): string => {
  const now = DateTime.now();
  const dateFormat = "dd/MM/yy";

  // Format for periods with end date
  if (endsOn) {
    // Check if the period is current (start date has passed, end date is in the future)
    if (startsOn <= now && endsOn >= now) {
      return `jusqu'au ${endsOn.toFormat(dateFormat)}`;
    }

    // Period is in the future
    return `à partir du ${startsOn.toFormat(dateFormat)} jusqu'au ${endsOn.toFormat(dateFormat)}`;
  }

  // Format for periods without end date
  if (startsOn > now) {
    return `à partir du ${startsOn.toFormat(dateFormat)}`;
  }

  // Start date has passed and no end date
  return "";
};

type CreateMonthSelectorOptionsType = {
  order?: "asc" | "desc";
  valueDateTimeFormat?: string;
  labelDateTimeFormat?: string;
};

/**
 * Create month selector options for date range selection
 * @param startMonthInUTC - The starting month (DateTime in UTC timezone)
 * @param endMonthInUTC - The ending month (DateTime in UTC timezone)
 * @param options - Configuration options for formatting and ordering
 * @returns Array of options with value and label for month selection
 */
export const createMonthSelectorOptions = (
  startMonthInUTC: DateTime,
  endMonthInUTC: DateTime,
  options: CreateMonthSelectorOptionsType = {}
): { value: string; label: string }[] => {
  const {
    order = "asc",
    valueDateTimeFormat = "yyyy-MM-dd",
    labelDateTimeFormat = "MMM yyyy",
  } = options;

  let delta = startMonthInUTC.startOf("month");
  const end = endMonthInUTC.startOf("month");
  const monthOptions: { value: string; label: string }[] = [];

  while (delta <= end) {
    monthOptions.push({
      value: delta.toFormat(valueDateTimeFormat),
      label: upperFirst(delta.toFormat(labelDateTimeFormat)),
    });
    delta = delta.plus({ months: 1 });
  }

  return order === "desc" ? monthOptions.reverse() : monthOptions;
};
